import CancelRounded from "@mui/icons-material/CancelRounded";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterListIcon from "@mui/icons-material/FilterList";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { Box, Button, useTheme } from "@mui/material";
import { useDebounce } from "@uidotdev/usehooks";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import ButtonDropdownMenu from "@/components/ButtonDropdownMenu";
import ButtonDropdownMultiselect from "@/components/ButtonDropdownMultiselect.tsx";
import AlphaSort from "@/components/Icons/AlphaSort";
import RecentSort from "@/components/Icons/RecentSort";
import StatusLight from "@/components/StatusLight";
import { FullInput } from "@/components/Utils";
import ChannelEmptyState from "@/components/WorkspaceChannelList/ChannelEmptyState";
import ChannelTypes from "@/components/WorkspaceChannelList/ChannelTypes";
import WorkspaceChannelLinkItem from "@/components/WorkspaceChannelList/WorkspaceChannelLinkItem";
import { client } from "@/config.ts";
import type { Account, WorkspaceMembership } from "@/db/types";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";
import UseStorage from "@/hooks/useStorage.ts";
import Locator from "@/locator";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { LiveQueryContext } from "@/models/LiveQueriesProvider";
import {
  accountsWithNoAvailabilities,
  ChannelList,
  ChannelOrderType,
  ChannelTypeType,
  fetchChannelList,
  myWorkspaceFeedGroups,
  nonGroupedChannels,
  workspaceInvitesAsChannels,
} from "@/models/repositories";
import { bulkUpsertDirectWsInvitation } from "@/models/repositories/DirectWsInvitation";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider.tsx";
import { UxContext } from "@/models/UxStateProvider";
import BasicButtonStyles from "@/theme/BasicButtonStyles.tsx";
import { grayColor, greenColor, ipcRendererCallback, redColor, removeIpcListener, yellowColor } from "@/utils";
import VirtualizedList from "../VirtualizedList.tsx";

type WorkspaceInviteTableSelect = {
  feedId?: string;
  workspaceId?: string;
  workspaceMembershipId?: string;
  email?: string | null;
  phoneNumber?: string | null;
  name?: string | null;
  claimedAt?: string | null;
  claimedBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
  contact?: string;
  date?: string;
  title?: string;
  accountId?: string;
  aliasMembershipId?: string;
  isAliasFeed?: boolean;
  membershipStatus?: "active" | "inactive";
  role: "limitedMember";
};

const transformInvitesIntoFilteredChannels = (data: WorkspaceInviteTableSelect[]) => {
  if (!data || data?.length === 0) {
    return {
      rows: [],
    };
  }

  return {
    rows: data?.map((invite) => ({
      id: invite.feedId,
      title: invite.contact,
      aliasChannel: invite.isAliasFeed,
      aliasOwnerAccountId: invite?.accountId ? invite.accountId : invite.aliasMembershipId,
      role: invite.role,
      workspaceId: invite.workspaceId,
      membershipStatus: invite.membershipStatus,
      hasMultipleAlias: false,
      subscribed: false,
    })),
  };
};

export default function WorkspaceChannelListContainer({ notificationsBanner }: { notificationsBanner: boolean }) {
  const { ampli } = useContext(TrackingContext);
  const { userReadOnlyMode } = useContext(UxContext);
  const { isWorkspaceLimitedMember, currentWorkspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { currentFeedId } = useContext(CurrentFeedContext);
  const { workspaceMembershipId } = useContext(LiveQueryContext);
  const limitedMember = isWorkspaceLimitedMember();
  const feedId = currentFeedId;
  const { myAccount } = useContext(MyAccountContext);
  const { getLocalStorage, setLocalStorage } = UseStorage("session");
  const theme = useTheme();
  const navigate = useNavigate();

  const [channelOrder, saveChannelOrder] = useSafeLocalStorage(
    `${currentWorkspaceId}-channel-sort-order`,
    "driver-activity" as ChannelOrderType,
  );
  const { normalButtonStyles } = BasicButtonStyles();

  const [channelFilterTypes, saveChannelFilterTypes] = useSafeLocalStorage(
    `${currentWorkspaceId}-channel-filter-types`,
    [],
  );

  const setActiveFilterTypes = (value: string) => {
    if (value === null) {
      saveChannelFilterTypes((prev) => []);
      return;
    }
    if (channelFilterTypes?.includes(value)) {
      saveChannelFilterTypes((prev) => prev.filter((type) => type !== value));
    } else {
      saveChannelFilterTypes((prev) => [...prev, value]);
    }
  };

  const filterTypes = [
    {
      text: "No Filter",
      value: null,
      icon: <FilterAltOffIcon sx={{ fontSize: "14px" }} />,
      active: channelFilterTypes?.length === 0 || channelFilterTypes === null,
    },
    {
      text: "Driving",
      value: "driving",
      icon: <StatusLight color={greenColor} size={12} />,
      active: channelFilterTypes?.includes("driving"),
    },
    {
      text: "Not Driving",
      value: "not-driving",
      icon: <StatusLight color={yellowColor} size={12} />,
      active: channelFilterTypes?.includes("not-driving"),
    },
    {
      text: "Hands-Free Off",
      value: "hands-free-off",
      icon: <StatusLight color={redColor} size={12} />,
      active: channelFilterTypes?.includes("hands-free-off"),
    },
    {
      text: "Volume Muted",
      value: "volume-muted",
      icon: <StatusLight color={redColor} size={12} />,
      active: channelFilterTypes?.includes("volume-muted"),
    },
    {
      text: "Offline",
      value: "offline",
      icon: <StatusLight color={grayColor} size={12} />,
      active: channelFilterTypes?.includes("offline"),
    },
    {
      text: "Never Logged In",
      value: "never-logged-in",
      icon: <StatusLight color={"#000000"} size={12} />,
      active: channelFilterTypes?.includes("never-logged-in"),
    },
  ];

  const [channelType, saveChannelType] = useSafeLocalStorage(
    `${currentWorkspaceId}-channel-sort-type`,
    "my-channels" as ChannelTypeType,
  );

  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchValue, 300);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>(searchValue);

  useEffect(() => {
    if (debouncedSearchTerm?.length === 0) {
      setDebouncedSearchValue(() => "");
    } else {
      setDebouncedSearchValue(() => debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  const tempOverRide = debouncedSearchValue?.length > 0 && !limitedMember ? "all-channels" : null;

  const dynamicChannelType = tempOverRide !== null ? tempOverRide : (channelType as ChannelTypeType);

  const feedGroupKey = `${currentWorkspaceId}-selectedFeedGroups`;

  const [selectedFeedGroups, setSelectedFeedGroups] = useState<string[]>(getLocalStorage(feedGroupKey) || []);

  const notLoggedInAccountsWithNoAvailability =
    useReactiveSelectQuery(
      (workspaceId: string) => accountsWithNoAvailabilities(workspaceId),
      [currentWorkspaceId],
      "WorkspaceChannelListContainer:accountsWithNoAvailabilities",
    ) ?? [];

  const { rows: transformedNotLoggedIn } = transformInvitesIntoFilteredChannels(
    notLoggedInAccountsWithNoAvailability as WorkspaceInviteTableSelect[],
  );

  const neverLoggedInUsers =
    useReactiveSelectQuery(
      (workspaceId: string) => workspaceInvitesAsChannels(workspaceId),
      [currentWorkspaceId],
      "WorkspaceChannelListContainer:workspaceInvitesAsChannels",
    ) ?? [];

  const { rows: neverLoggedInChannelList } = transformInvitesIntoFilteredChannels(
    neverLoggedInUsers as WorkspaceInviteTableSelect[],
  );

  const combined = [...neverLoggedInChannelList, ...transformedNotLoggedIn];

  const loadedInvites = useRef<boolean>(false);
  const fetchAndUpdateWorkspaceInvites = async () => {
    if (loadedInvites.current) {
      return;
    }
    loadedInvites.current = true;
    const resp = await client.getWorkspaceInvitations(currentWorkspaceId);
    if (resp?.directInvitations) {
      await bulkUpsertDirectWsInvitation(resp?.directInvitations || []);
    }
  };

  const regularChannelList =
    useReactiveSelectQuery(
      (p: {
        channelOrder: ChannelOrderType;
        channelType: ChannelTypeType | null;
        workspaceId: string;
        myCurrentWorkspaceMembership: WorkspaceMembership;
        searchValue: string;
        myAccount: Account;
        channelFilters: string[] | null;
        selectedFeedGroups?: string[];
      }) =>
        fetchChannelList({
          channelOrder: p.channelOrder,
          channelType: p.channelType as ChannelTypeType,
          workspaceId: p.workspaceId,
          myCurrentWorkspaceMembership: p.myCurrentWorkspaceMembership,
          searchValue: p.searchValue,
          myAccount: p.myAccount,
          channelFilters: p.channelFilters,
          selectedFeedGroups: p.selectedFeedGroups,
        }),
      [
        {
          channelOrder,
          channelType: dynamicChannelType,
          workspaceId: currentWorkspaceId,
          myCurrentWorkspaceMembership,
          searchValue: debouncedSearchValue,
          myAccount,
          channelFilters: channelFilterTypes,
          selectedFeedGroups,
        },
      ],
      `WorkspaceChannelListContainer:fetchChannelList:${currentWorkspaceId}:${dynamicChannelType}:${channelOrder}`,
    ) ?? [];

  useEffect(() => {
    if (channelFilterTypes?.includes("never-logged-in")) {
      fetchAndUpdateWorkspaceInvites();
    }
  }, [channelFilterTypes]);
  const channelList = channelFilterTypes?.includes("never-logged-in") ? combined : regularChannelList;

  const nonGroupedChannelsCount =
    useReactiveSelectQuery(
      (workspaceId: string) => nonGroupedChannels(workspaceId),
      [currentWorkspaceId],
      "WorkspaceChannelListContainer:nonGroupedChannels",
    ) ?? [];

  const feedGroups =
    useReactiveSelectQuery(
      (workspaceId: string, workspaceMembershipId: string) => myWorkspaceFeedGroups(workspaceId, workspaceMembershipId),
      [currentWorkspaceId, myCurrentWorkspaceMembership?.id],
      "WorkspaceChannelListContainer:myWorkspaceFeedGroups",
    ) ?? [];

  if (
    nonGroupedChannelsCount?.length > 0 &&
    feedGroups?.length > 1 &&
    !feedGroups?.some((group) => group.id === "non-grouped-channels")
  ) {
    feedGroups.push({
      groupId: "non-grouped-channels",
      id: "non-grouped-channels",
      name: "Non-Grouped Channels",
    });
  }

  const clearFeedGroups = () => {
    setSelectedFeedGroups(() => []);
    setLocalStorage({ key: feedGroupKey, value: "" });
  };

  const toggleActiveFeedGroup = (groupId: string) => {
    if (selectedFeedGroups.includes(groupId)) {
      setSelectedFeedGroups((prev) => {
        const selectedValues = prev.filter((id) => id !== groupId);
        setLocalStorage({ key: feedGroupKey, value: selectedValues });
        return selectedValues;
      });
    } else {
      setSelectedFeedGroups((prev) => {
        const selectedValues = [...prev, groupId];
        setLocalStorage({ key: feedGroupKey, value: selectedValues });
        return selectedValues;
      });
    }
  };
  const activeFeedGroups =
    feedGroups?.map((group) => ({
      ...group,
      text: group.name,
      value: group.id,
      active: selectedFeedGroups.includes(group.id),
    })) || [];

  const channels = useMemo(
    () =>
      channelList?.map((channel) => ({
        ...channel,
        hasMultipleAlias: (channel.aliasCount ?? 0) > 1,
      })),
    [channelList],
  );

  const unreadCount = useMemo(
    () =>
      regularChannelList?.filter((channel: ChannelList) => channel.unread !== null && channel.subscribed)?.length ?? 0,
    [regularChannelList],
  );

  const navigationHandler = (url: string) => {
    ampli.electronPipRedirectToApp();
    navigate(url);
  };

  useEffect(() => {
    ipcRendererCallback("navigate-to-url", (_, url: string) => navigationHandler(url));
    return () => {
      removeIpcListener("navigate-to-url", () => navigationHandler);
    };
  }, []);

  const [searchInputFullWidth, setSearchInputFullWidth] = useState<boolean>(false);

  const handleInputFocus = (value: boolean) => {
    if (value === true && !searchInputFullWidth) {
      setSearchInputFullWidth(() => true);
    } else if (value === false && debouncedSearchValue.length === 0) {
      setSearchInputFullWidth(() => false);
    }
  };

  const renderChild = (channel: ChannelList) => {
    return (
      <WorkspaceChannelLinkItem
        limitedMember={limitedMember}
        readOnlyMode={userReadOnlyMode}
        key={channel.id}
        active={channel.id === feedId}
        channel={channel}
        workspaceMembershipId={workspaceMembershipId}
      />
    );
  };

  const height = notificationsBanner ? "calc(100vh - 435px)" : "calc(100vh - 345px)";

  return (
    <Box
      className={`
        workspace-channel-list-container
        ${notificationsBanner ? "has-banner" : ""}
        ${userReadOnlyMode ? "read-only-mode" : ""}
      `}
    >
      <Box sx={{ display: "flex", p: 2, gap: 2 }}>
        <Box sx={{ flexGrow: "1", position: "relative" }}>
          <FullInput
            onFocus={() => handleInputFocus(true)}
            onBlur={() => handleInputFocus(false)}
            value={searchValue}
            inputProps={{
              placeholder: "Search",
            }}
            formControlProps={{
              sx: {
                "& .MuiInputBase-input": {
                  paddingLeft: "1rem !important",
                },
              },
            }}
            callback={(e) => {
              setSearchValue(e.target.value);
            }}
          />
          <Box className="channel-search-clear-button buttons">
            {searchValue?.length ? (
              <button
                type="button"
                aria-label={Locator.workspaceNav.channels.find.clear}
                onClick={() => {
                  setSearchValue("");
                  setSearchInputFullWidth(() => false);
                }}
              >
                <CancelRounded />
              </button>
            ) : (
              <SearchIcon sx={{ color: searchValue?.length > 0 ? "#fff" : "#ccc" }} />
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "0.25rem" }}>
          {activeFeedGroups?.length > 0 && (
            <Box className="channel-options-button buttons">
              <ButtonDropdownMultiselect
                indicateActive={activeFeedGroups?.some((group) => group.active)}
                changeValue={(id: string) => toggleActiveFeedGroup(id)}
                values={activeFeedGroups}
                title={"Available Groups"}
                icon={<GroupAddIcon />}
              >
                <>
                  <Button
                    sx={{
                      ...normalButtonStyles,
                      py: 0.5,
                      px: 1,
                      mb: 1,
                      fontWeight: "normal",
                      boxShadow:
                        selectedFeedGroups?.length > 0 ? `0 0 0 1px ${theme.palette.brand.primary.main}` : "none",
                    }}
                    onClick={() => clearFeedGroups()}
                    disabled={selectedFeedGroups?.length === 0}
                  >
                    Clear Selected Groups
                  </Button>
                </>
              </ButtonDropdownMultiselect>
            </Box>
          )}
          <Box className="channel-options-button buttons">
            <ButtonDropdownMultiselect
              indicateActive={channelFilterTypes?.length > 0}
              changeValue={(value: string) => setActiveFilterTypes(value)}
              values={filterTypes}
              title={Locator.workspaceNav.channels.dropdowns.filter.title}
              icon={<FilterListIcon />}
            />
          </Box>

          <Box className="channel-options-button buttons">
            <ButtonDropdownMenu
              activeValue={channelOrder}
              changeValue={(value: ChannelOrderType) => saveChannelOrder(() => value)}
              values={[
                {
                  text: "Alphabetically",
                  value: "alpha-asc",
                  icon: <AlphaSort />,
                  active: true,
                },
                {
                  text: "By most recent",
                  value: "driver-activity",
                  icon: <RecentSort />,
                  active: false,
                },
              ]}
              title={Locator.workspaceNav.channels.dropdowns.sort.title}
              icon={<SwapVertIcon />}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ mb: 2, px: 1 }}>
        <ChannelTypes
          workspaceMembershipId={workspaceMembershipId}
          tempOverRide={tempOverRide}
          limitedMember={limitedMember}
          unreadCount={unreadCount}
          changeChannelType={(channelType: ChannelTypeType) => {
            saveChannelType(() => channelType);
            if (channelType !== "all-channels") {
              setSearchValue("");
            }
          }}
          activeChannelType={channelType}
        />
      </Box>

      <Box className="workspace-channel-list-items" sx={{ height }}>
        <VirtualizedList
          id="workspaceChannelListContainer"
          data={channels}
          childComponent={renderChild}
          shift={false}
          itemSize={35}
          emptyState={
            <ChannelEmptyState
              activeFilter={null}
              limitedMember={limitedMember}
              hasSearch={searchValue?.length > 0}
              channelType={channelType}
              changeChannelType={(channelType: ChannelTypeType) => {
                saveChannelType(channelType);
              }}
              clearFilters={() => {
                saveChannelFilterTypes(() => null);
                setSearchValue(() => "");
              }}
            />
          }
        />
      </Box>
    </Box>
  );
}
